/**
 * "Update Available!" — Update now / Later (in-game parity, client
 * 2026-10-07).
 *
 * The prompt the coach sees the moment an update is HELD (see
 * pendingUpdate.ts). The red dots say an update is waiting; this is the modal
 * that lets the coach take it or not. Pure and dependency-free so the
 * visibility rules are testable without a browser:
 *
 *  - it opens when an update becomes pending;
 *  - "Later" closes it for THAT update — the dots stay, and the drawer's
 *    Check For Updates still takes it — and it must not nag again;
 *  - a NEW update (the flag clears and rises again) may prompt again.
 */

export interface UpdatePromptState {
  /** An update is installed and waiting (mirrors the pendingUpdate flag). */
  pending: boolean;
  /** The coach pressed "Later" on the currently pending update. */
  dismissed: boolean;
}

export const INITIAL_UPDATE_PROMPT: UpdatePromptState = {
  pending: false,
  dismissed: false,
};

export type UpdatePromptEvent =
  /**
   * The pendingUpdate flag changed. `dismissed`: whether this tab has said
   * Later to the waiting update, read fresh as the flag rises.
   */
  | { type: "pending"; pending: boolean; dismissed?: boolean }
  /** The coach chose "Later". */
  | { type: "later" };

export function reduceUpdatePrompt(
  state: UpdatePromptState,
  event: UpdatePromptEvent
): UpdatePromptState {
  switch (event.type) {
    case "pending":
      if (event.pending === state.pending) return state;
      // The flag clearing means the update was taken (or is gone); a later
      // rise is a different update and gets a fresh prompt. A rise on a page
      // that already said Later (the flag re-raised at boot) keeps it — read
      // from storage as it rises, so a Later left over from an update that
      // another tab took (and the boot check forgot) can't hide a new one.
      return {
        pending: event.pending,
        dismissed: event.pending ? (event.dismissed ?? state.dismissed) : false,
      };
    case "later":
      return state.pending ? { ...state, dismissed: true } : state;
  }
}

export const isUpdatePromptVisible = (state: UpdatePromptState): boolean =>
  state.pending && !state.dismissed;

export const UPDATE_PROMPT_COPY = {
  title: "Update Available!",
  // The body, with the product name set in bold.
  bodyBefore: "A new version of ",
  bodyBrand: "Modern Football",
  bodyAfter:
    " is ready. Download the latest update now to get the newest improvements and features.",
  failed: "The update could not be applied. Try Check For Updates in the menu.",
  confirm: "Update now",
  dismiss: "Later",
  updating: "Updating...",
} as const;

/**
 * "Later" must survive a reload: the waiting worker is found again at every
 * boot and re-raises the flag, so an in-memory dismissal re-asked the coach
 * right after any reload. sessionStorage: per tab, gone when the tab closes,
 * which is also when the waiting worker activates on its own. Cleared when
 * the flag clears.
 */
export const UPDATE_PROMPT_DISMISSED_KEY = "mofo:update-prompt-dismissed";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const storage = (): StorageLike | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

export function readUpdatePromptDismissed(store: StorageLike | null = storage()): boolean {
  try {
    return store?.getItem(UPDATE_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberUpdatePromptDismissed(store: StorageLike | null = storage()): void {
  try {
    store?.setItem(UPDATE_PROMPT_DISMISSED_KEY, "1");
  } catch {
    // Private mode / quota — the in-memory state still holds for this page.
  }
}

export function forgetUpdatePromptDismissed(store: StorageLike | null = storage()): void {
  try {
    store?.removeItem(UPDATE_PROMPT_DISMISSED_KEY);
  } catch {
    // nothing to forget
  }
}

/** The reducer's starting point for a page that may have said Later already. */
export const initialUpdatePromptState = (dismissed: boolean): UpdatePromptState => ({
  pending: false,
  dismissed,
});

/**
 * The prompt waits while ANOTHER dialog is up. An update can land at any
 * moment — the worker is probed on every return to the app — and a modal that
 * mounts on top of an open dialog puts "Update now" under a tap that was aimed
 * at something else. Nothing is lost by waiting: the flag and the dots stay,
 * and the prompt opens once the other dialog closes.
 */
export const isAnotherDialogOpen = (
  root: { querySelector: (s: string) => unknown } | null = typeof document === "undefined"
    ? null
    : document
): boolean => root?.querySelector('[role="dialog"]') != null;
